import { useQueryClient } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { RefreshAlert } from "./RefreshAlert"

// Singleton stores
const activeDocumentQueries = new Set<string>()
const deletedDocuments = new Set<string>()
const listeners = new Set<() => void>()

// Utility functions
function getQueryIdentifier(collectionName: string, docId: string) {
   return `document/${collectionName}/${docId}`
}

/**
 * Call this when a document is deleted in Firestore
 */
export function notifyDocumentAsDeleted(collectionName: string, docId: string) {
   const queryIdentifier = getQueryIdentifier(collectionName, docId)
   // Only mark as deleted if the document is currently being viewed
   if (activeDocumentQueries.has(queryIdentifier)) {
      deletedDocuments.add(`${collectionName}/${docId}`)
      listeners.forEach((listener) => listener())
   }
}

/**
 * Component that monitors active document queries
 */
export function QueryMonitor() {
   const queryClient = useQueryClient()
   const location = useLocation()
   
   useEffect(() => {
      
      if (location.pathname === '/admin/absences-et-retards') return
      if (location.pathname.startsWith('/admin/resume-du-jeune')) return

      const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
         if (!event?.query) return

         const queryKey = event.query.queryKey
         const queryType = queryKey[0]

         if (queryType !== "document") return

         const identifier = queryKey.join("/")
         const isActive = event.query.isActive()

         // Track when queries become active/inactive
         if (isActive && !activeDocumentQueries.has(identifier)) {
            activeDocumentQueries.add(identifier)
            // logger(`[${queryType}/${identifier}] Query became active`);
         } else if (!isActive && activeDocumentQueries.has(identifier)) {
            activeDocumentQueries.delete(identifier)
            // logger(`[${queryType}/${identifier}] Query became inactive`);
         }
      })

      return () => {
         unsubscribe()
      }
   }, [queryClient, location])

   return null
}

/**
 * Alert component that shows when a document is deleted
 */
export function DeletedDocumentAlert() {
   const navigate = useNavigate()
   const [deletedPaths, setDeletedPaths] = useState<string[]>([])

   useEffect(() => {
      const handleDocumentDeleted = () => {
         setDeletedPaths(Array.from(deletedDocuments))
      }

      listeners.add(handleDocumentDeleted)
      return () => {
         listeners.delete(handleDocumentDeleted)
      }
   }, [])

   if (deletedPaths.length === 0) {
      return null
   }

   const handleNavigateToCollection = () => {
      // const [firstDeletedPath] = deletedPaths
      // const collectionPath = firstDeletedPath.split("/")[0]

      // Clear the deleted documents
      deletedDocuments.clear()
      setDeletedPaths([])

      // navigate(`/${collectionPath}`)
      navigate(`/`)
   }

   return (
      <div style={{ position: "relative", zIndex: 1001 }}>
         <RefreshAlert
            onRefresh={handleNavigateToCollection}
            isFetching={false}
            message="Le document a été supprimé par un utilisateur"
            variant="warning"
            buttonText="Retour"
         />
      </div>
   )
}
